import React, { useState } from 'react';
import { X, Filter } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'Campaign', 'Professional', 'Community'];

  const images = [
    {
      url: 'https://customer-assets.emergentagent.com/job_c8ba1316-fe9a-4786-bbb1-586136be868e/artifacts/38oatjps_Elegance%20By%20AG-2570.jpg',
      alt: 'Wanda Roman - Leadership Portrait',
      category: 'Professional',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_c8ba1316-fe9a-4786-bbb1-586136be868e/artifacts/5ch00nxt_Elegance%20By%20AG-2571.jpg',
      alt: 'Wanda Roman - Professional Campaign Photo',
      category: 'Professional',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_c8ba1316-fe9a-4786-bbb1-586136be868e/artifacts/p74jqqxy_Elegance%20By%20AG-2560.jpg',
      alt: 'Wanda Roman - Community Engagement',
      category: 'Community',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_c8ba1316-fe9a-4786-bbb1-586136be868e/artifacts/3bloytv3_Elegance%20By%20AG-2554.jpg',
      alt: 'Wanda Roman - Campaign Trail',
      category: 'Campaign',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_c8ba1316-fe9a-4786-bbb1-586136be868e/artifacts/w3r3qb3t_Elegance%20By%20AG-2579.jpg',
      alt: 'Wanda Roman - Leadership in Action',
      category: 'Professional',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_wanda-for-kissimmee/artifacts/len3i1sn_Elegance%20By%20AG-2574.jpg',
      alt: 'Wanda Roman - Campaign Events',
      category: 'Campaign',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_wanda-for-kissimmee/artifacts/1eluo2p5_Elegance%20By%20AG-2553.jpg',
      alt: 'Wanda Roman - Community Connection',
      category: 'Community',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_wanda-for-kissimmee/artifacts/bira1rby_Elegance%20By%20AG-2558.jpg',
      alt: 'Wanda Roman - Professional Portrait',
      category: 'Professional',
    },
  ];

  const filteredImages = activeCategory === 'All' 
    ? images 
    : images.filter(img => img.category === activeCategory);

  return (
    <div className="min-h-screen">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50/80 via-white to-purple-100/40 -z-10"></div>
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-purple-700 to-purple-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
              Campaign Gallery
            </h1>
            <div className="w-24 h-1.5 bg-amber-400 mx-auto mb-6"></div>
            <p className="text-xl md:text-2xl text-white/95 leading-relaxed">
              Moments from the campaign trail - connecting with our community, sharing our vision, and building a stronger Kissimmee together
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-40 bg-white/95 backdrop-blur-md shadow-md py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-purple-700 mr-2" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-purple-700 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-purple-100 hover:text-purple-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 30% 40%, rgba(124, 58, 237, 0.1) 0%, transparent 50%),
                           radial-gradient(circle at 70% 70%, rgba(167, 139, 250, 0.08) 0%, transparent 50%)`
        }}></div>
        
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Image Count */}
          <div className="text-center mb-8">
            <p className="text-gray-600">
              Showing <span className="font-bold text-purple-700">{filteredImages.length}</span> {activeCategory === 'All' ? 'photos' : `${activeCategory.toLowerCase()} photos`}
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((image, index) => (
              <div
                key={index}
                className="group relative cursor-pointer overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 aspect-square bg-gray-100"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90 via-purple-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <span className="inline-block px-3 py-1 bg-purple-600 rounded-full text-xs font-semibold mb-2 text-white w-fit">
                    {image.category}
                  </span>
                  <p className="font-semibold text-lg text-white">{image.alt}</p>
                </div>
                {/* Zoom indicator */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-purple-50/50 to-purple-100/60"></div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.1) 0%, transparent 70%)`
        }}></div>
        
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Be Part of Our Story
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Join us on the campaign trail and help build a stronger Kissimmee. Your involvement makes all the difference.
            </p>
            <a
              href="/#contact"
              className="inline-block bg-purple-700 hover:bg-purple-800 text-white font-bold text-lg px-12 py-4 rounded-lg shadow-xl transition-all duration-300 hover:scale-105"
            >
              Get Involved Today
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-purple-300 transition-colors z-10 bg-white/10 backdrop-blur-sm rounded-full p-3 hover:bg-white/20"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <div className="relative max-w-7xl w-full">
            <img
              src={selectedImage.url}
              alt={selectedImage.alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg mx-auto"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8 rounded-b-lg">
              <span className="inline-block px-4 py-2 bg-purple-600 rounded-full text-sm font-semibold mb-3 text-white">
                {selectedImage.category}
              </span>
              <p className="text-white font-semibold text-2xl">{selectedImage.alt}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
